/**
 * Auth session store (client state). Source of truth for the current
 * user and JWT tokens.
 *
 * Only the JWT tokens are persisted to localStorage — the `user` profile is
 * deliberately NOT persisted. On every load the session is re-hydrated from
 * the stored token and the profile is re-fetched from `/auth/me` (see
 * `useSessionBootstrap`), so the cached profile can never drift from the
 * backend. The axios client reads tokens from here synchronously via
 * `useAuthStore.getState()`.
 */
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { AuthTokens, AuthUser, TenantDetail } from '../types'
import { STORAGE_KEYS } from '@/config/app.config'

interface AuthState {
  user: AuthUser | null
  /** Cached tenant detail (reporting group + branches), fetched on demand. */
  tenant: TenantDetail | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean

  /** Set the full session after login. */
  setSession: (payload: { user: AuthUser; tokens: AuthTokens }) => void
  /**
   * Establish a session from a single bearer token (no refresh token) —
   * used for the JWT handed off via the redirect URL hash.
   */
  setSessionFromToken: (payload: { user: AuthUser; accessToken: string }) => void
  /** Replace tokens (used by the refresh flow). */
  setTokens: (tokens: AuthTokens) => void
  /** Update the cached user (e.g. after fetching /auth/me). */
  setUser: (user: AuthUser) => void
  /** Cache the tenant detail (e.g. after fetching /admin/tenants/:id). */
  setTenant: (tenant: TenantDetail | null) => void
  /** Clear everything (logout / refresh failure). */
  clearSession: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setSession: ({ user, tokens }) =>
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
        }),

      setSessionFromToken: ({ user, accessToken }) =>
        set({
          user,
          accessToken,
          refreshToken: null,
          isAuthenticated: true,
        }),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        }),

      setUser: (user) => set({ user, isAuthenticated: true }),

      setTenant: (tenant) => set({ tenant }),

      clearSession: () =>
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: STORAGE_KEYS.auth,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Persist ONLY the tokens. The user profile and `isAuthenticated` flag
      // are runtime-only — re-derived on load by re-fetching `/auth/me`.
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      // v0 sessions also persisted `user`/`isAuthenticated`; keep only the
      // tokens so the profile is always re-fetched rather than restored stale.
      migrate: (persisted) => {
        const prev = persisted as Partial<AuthState> | null
        return {
          accessToken: prev?.accessToken ?? null,
          refreshToken: prev?.refreshToken ?? null,
        }
      },
    },
  ),
)

/** Non-reactive accessor for use outside React (interceptors, guards). */
export const authStore = {
  getState: useAuthStore.getState,
}
